export class SDKAdapter {
    /** Register a callback for execution plan / tool progress events. Returns an unsubscribe function. */
    onProgress(callback) {
        this.progressCallbacks.add(callback);
        return () => {
            this.progressCallbacks.delete(callback);
        };
    }
    emitProgress(event) {
        this.progressCallbacks.forEach((cb) => {
            try {
                cb(event);
            }
            catch (e) {
                /* ignore */
            }
        });
    }
    constructor(config) {
        this.context = null;
        this.contextChangeCallbacks = new Set();
        this.user = null;
        this.accessToken = null;
        this.userContextData = {};
        this.toolExecutor = null;
        this.confirmationHandler = null;
        this.toolCatalogSynced = false;
        this.lastToolCatalog = [];
        this.jwtToken = null;
        this.contextCache = null;
        this.contextOptimized = true;
        this.progressCallbacks = new Set();
        this.userMessageCallback = null;
        this.currentChannelId = null;
        this.apiBaseUrl = config.apiBaseUrl || 'https://balancy-ai.balancy.cloud';
        this.assetsBaseUrl =
            config.assetsBaseUrl || 'https://balancy-ai.balancy.cloud/sdk/latest';
        this.context = config.initialContext || null;
        this.user = config.user || null;
        this.accessToken = config.accessToken || null;
        this.toolExecutor = config.toolExecutor || null;
        this.confirmationHandler = config.confirmationHandler || null;
        this.contextCache = config.contextCache || null;
    }
    /** Set the callback fired once per user-sent message (for analytics). */
    setUserMessageCallback(cb) {
        this.userMessageCallback = cb;
    }
    getContext() {
        return this.context;
    }
    /**
     * Update context (called by Balancy when user navigates)
     */
    updateContext(context) {
        const oldContext = this.context;
        this.context = context;
        // Only notify if context actually changed
        if (JSON.stringify(oldContext) !== JSON.stringify(context)) {
            this.contextChangeCallbacks.forEach((callback) => {
                try {
                    callback(context);
                }
                catch (error) {
                    console.error('[SDKAdapter] Error in context change callback:', error);
                }
            });
        }
    }
    onContextChange(callback) {
        this.contextChangeCallbacks.add(callback);
        // Call immediately with current context if available
        if (this.context) {
            callback(this.context);
        }
        // Return unsubscribe function
        return () => {
            this.contextChangeCallbacks.delete(callback);
        };
    }
    /**
     * Build common auth headers for API requests
     */
    getAuthHeaders() {
        const headers = {
            'Content-Type': 'application/json',
        };
        // Always send Balancy headers when available — the main chat endpoints
        // use the legacy auth middleware that expects these headers.
        if (this.user?.email) {
            headers['X-Balancy-Email'] = this.user.email;
            if (this.accessToken) {
                headers['X-Balancy-Token'] = this.accessToken;
            }
        }
        // Also send JWT if available (used by auth-specific endpoints like accept-terms)
        if (this.jwtToken) {
            headers['Authorization'] = `Bearer ${this.jwtToken}`;
        }
        else if (this.accessToken && !this.user?.email) {
            headers['Authorization'] = `Bearer ${this.accessToken}`;
        }
        return headers;
    }
    /**
     * Send a chat message using SSE (Server-Sent Events).
     *
     * The backend streams events:
     *   - event: tool_call_request  → SDK executes tool locally, POSTs result back
     *   - event: reply              → final LLM response
     *   - event: error              → error occurred
     *   - event: done               → stream complete
     *
     * Falls back to the legacy JSON endpoint if SSE is not supported.
     */
    async sendMessage(message, _context) {
        try {
            console.log('[SDKAdapter] Sending message with userContext:', this.userContextData);
            // Fire the user-message callback for analytics (before the network call)
            if (this.userMessageCallback) {
                try {
                    this.userMessageCallback({
                        messageLength: message.length,
                        chatContext: this.userContextData?.chatContext || 'cms',
                        model: this.userContextData
                            ?.model || 'default',
                        channelId: this.currentChannelId,
                    });
                }
                catch (e) {
                    console.warn('[SDKAdapter] onUserMessage callback error:', e);
                }
            }
            const headers = this.getAuthHeaders();
            // Use SSE streaming endpoint when client-side tools are registered
            if (this.toolCatalogSynced) {
                return await this.sendMessageSSE(message, headers);
            }
            // Legacy: standard JSON POST for backward compatibility
            return await this.sendMessageLegacy(message, headers);
        }
        catch (error) {
            console.error('[SDKAdapter] Error sending message:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Network error',
            };
        }
    }
    /**
     * Legacy non-streaming message send (backward compatible)
     */
    async sendMessageLegacy(message, headers) {
        const response = await fetch(`${this.apiBaseUrl}/api/chat/docs`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                message,
                channelId: this.currentChannelId,
                userContext: this.userContextData,
                chatContext: this.userContextData?.chatContext,
            }),
        });
        if (response.ok) {
            const data = await response.json();
            if (data.channelId) {
                this.currentChannelId = data.channelId;
            }
            return {
                success: true,
                reply: data.reply,
                channelId: data.channelId,
                messageId: data.messageId,
                suggestions: data.suggestions,
                usage: data.usage
                    ? {
                        totalTokens: data.usage.totalTokens,
                        promptTokens: data.usage.promptTokens,
                        completionTokens: data.usage.completionTokens,
                    }
                    : undefined,
                credits: data.credits,
            };
        }
        else {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                error: errorData.error?.message || `HTTP ${response.status}`,
            };
        }
    }
    /**
     * SSE-based message send with client-side tool execution support.
     *
     * Uses fetch() with streaming body reader to process SSE events.
     * When a tool_call_request event arrives, executes the tool locally
     * and POSTs the result back to the backend to continue the LLM loop.
     */
    async sendMessageSSE(message, headers) {
        console.log('[SDKAdapter] sendMessageSSE: start');
        // Re-sync tool catalog before each request to handle server restarts
        if (this.lastToolCatalog.length > 0) {
            console.log('[SDKAdapter] sendMessageSSE: syncing tool catalog');
            await this.syncToolCatalog(this.lastToolCatalog, this.lastToolContractVersion);
        }
        // Refresh cache before sending (get latest data for all cached items)
        if (this.contextOptimized &&
            this.contextCache &&
            this.contextCache.size > 0) {
            console.log(`[SDKAdapter] sendMessageSSE: refreshing cache (${this.contextCache.size} items)`);
            try {
                // Add a timeout to prevent hanging
                const CACHE_REFRESH_TIMEOUT = 10000; // 10 seconds
                await Promise.race([
                    this.contextCache.refreshAll(),
                    new Promise((_, reject) => setTimeout(() => reject(new Error('Cache refresh timed out')), CACHE_REFRESH_TIMEOUT)),
                ]);
                console.log(`[SDKAdapter] Cache refreshed: ${this.contextCache.size} items`);
            }
            catch (error) {
                console.warn('[SDKAdapter] Cache refresh failed:', error);
                // Continue with stale cache data rather than blocking the message
            }
        }
        // Build request body — include cache when optimization is on
        const requestBody = {
            message,
            channelId: this.currentChannelId,
            userContext: this.userContextData,
            chatContext: this.userContextData?.chatContext,
        };
        if (this.contextOptimized &&
            this.contextCache &&
            this.contextCache.size > 0) {
            requestBody.cache = this.contextCache.getAll();
            console.log(`[SDKAdapter] sendMessageSSE: including ${this.contextCache.size} cache entries`);
        }
        console.log('[SDKAdapter] sendMessageSSE: sending fetch request');
        const response = await fetch(`${this.apiBaseUrl}/api/chat/docs/stream`, {
            method: 'POST',
            headers: {
                ...headers,
                Accept: 'text/event-stream',
            },
            body: JSON.stringify(requestBody),
        });
        console.log(`[SDKAdapter] sendMessageSSE: response status ${response.status}`);
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                error: errorData.error?.message || `HTTP ${response.status}`,
            };
        }
        // Parse SSE stream
        console.log('[SDKAdapter] sendMessageSSE: processing SSE stream');
        return await this.processSSEStream(response);
    }
    /**
     * Process the SSE event stream from the backend.
     * Handles tool_call_request events by executing tools locally
     * and sending results back.
     */
    async processSSEStream(response) {
        const reader = response.body?.getReader();
        if (!reader) {
            return { success: false, error: 'No response body for SSE stream' };
        }
        const decoder = new TextDecoder();
        let buffer = '';
        let result = {
            success: false,
            error: 'Stream ended without reply',
        };
        let gotFinalResult = false;
        // Inactivity timeout: if no data arrives for 90 seconds, abort the stream.
        // This prevents the SDK from hanging forever when the backend is stuck
        // (e.g., LLM API timeout, network issue).
        const SSE_INACTIVITY_TIMEOUT = 90000;
        let inactivityTimer = null;
        // Pause flag: when true, the inactivity timer is suspended (e.g., during
        // tool execution or user confirmation, where no SSE data is expected).
        let inactivityPaused = false;
        const resetInactivityTimer = () => {
            if (inactivityTimer)
                clearTimeout(inactivityTimer);
            if (inactivityPaused)
                return;
            inactivityTimer = setTimeout(() => {
                console.error(`[SDKAdapter] SSE stream inactive for ${SSE_INACTIVITY_TIMEOUT / 1000}s, aborting`);
                reader.cancel().catch(() => { });
            }, SSE_INACTIVITY_TIMEOUT);
        };
        const pauseInactivityTimer = () => {
            inactivityPaused = true;
            if (inactivityTimer) {
                clearTimeout(inactivityTimer);
                inactivityTimer = null;
            }
        };
        const resumeInactivityTimer = () => {
            inactivityPaused = false;
            resetInactivityTimer();
        };
        try {
            resetInactivityTimer();
            for (;;) {
                const { done, value } = await reader.read();
                if (done)
                    break;
                resetInactivityTimer();
                buffer += decoder.decode(value, { stream: true });
                // Parse SSE events from buffer
                const events = this.parseSSEBuffer(buffer);
                buffer = events.remaining;
                for (const event of events.parsed) {
                    console.log(`[SDKAdapter] SSE event: ${event.event}`);
                    // Skip processing once we have a final result
                    if (gotFinalResult)
                        continue;
                    // Pause inactivity timer for events that involve async work
                    // (tool execution, user confirmation) where no SSE data is expected.
                    const asyncEvents = [
                        'tool_call_request',
                        'tool_confirmation_required',
                    ];
                    if (asyncEvents.includes(event.event)) {
                        pauseInactivityTimer();
                    }
                    const eventResult = await this.handleSSEEvent(event);
                    // Resume inactivity timer after async event handling
                    if (asyncEvents.includes(event.event)) {
                        resumeInactivityTimer();
                    }
                    // handleSSEEvent returns null for non-final events (tool_call_request, done, unknown)
                    if (eventResult !== null) {
                        result = eventResult;
                        gotFinalResult = true;
                        console.log(`[SDKAdapter] Got final result: success=${result.success}`);
                    }
                }
            }
        }
        finally {
            if (inactivityTimer)
                clearTimeout(inactivityTimer);
            reader.releaseLock();
        }
        // If stream ended due to inactivity timeout, provide a clear error
        if (!gotFinalResult && result.error === 'Stream ended without reply') {
            result.error =
                'Request timed out — the server took too long to respond. Please try again.';
        }
        return result;
    }
    /**
     * Parse SSE events from a text buffer.
     * Returns parsed events and any remaining incomplete data.
     */
    parseSSEBuffer(buffer) {
        const parsed = [];
        const lines = buffer.split('\n');
        let currentEvent = '';
        let currentData = '';
        let lastCompleteIndex = -1;
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            if (line.startsWith('event: ')) {
                currentEvent = line.slice(7).trim();
            }
            else if (line.startsWith('data: ')) {
                currentData = line.slice(6);
            }
            else if (line === '' && currentEvent) {
                // Empty line = end of event
                parsed.push({ event: currentEvent, data: currentData });
                currentEvent = '';
                currentData = '';
                lastCompleteIndex = i;
            }
            else if (line === '') {
                lastCompleteIndex = i;
            }
        }
        // Return unparsed remainder
        const remaining = lastCompleteIndex < lines.length - 1
            ? lines.slice(lastCompleteIndex + 1).join('\n')
            : '';
        return { parsed, remaining };
    }
    /**
     * Handle a single SSE event.
     * Returns a ChatResponse when the event is a final reply/error.
     */
    async handleSSEEvent(event) {
        switch (event.event) {
            case 'tool_call_request': {
                // Backend is asking us to execute a client-side tool
                const request = JSON.parse(event.data);
                console.log(`[SDKAdapter] Tool call request: ${request.toolName}`, request.arguments);
                // Execute tool locally
                let toolResult;
                if (this.toolExecutor) {
                    console.log(`[SDKAdapter] Executing tool: ${request.toolName}`);
                    toolResult = await this.toolExecutor(request.toolName, request.arguments);
                    console.log(`[SDKAdapter] Tool executed: ${request.toolName} success=${toolResult.success}`);
                }
                else {
                    toolResult = {
                        success: false,
                        error: 'No tool executor registered',
                    };
                }
                // Populate cache from tool results when optimization is enabled
                if (this.contextOptimized &&
                    this.contextCache &&
                    toolResult.success &&
                    toolResult.result) {
                    this.populateCacheFromToolResult(request.toolName, request.arguments, toolResult.result);
                    console.log(`[SDKAdapter] Cache updated after ${request.toolName}, cache size: ${this.contextCache.size}`);
                }
                // Send result back to backend
                console.log(`[SDKAdapter] Sending tool result for ${request.callId}`);
                await this.sendToolResult(request.streamId, request.callId, toolResult);
                console.log(`[SDKAdapter] Tool result sent for ${request.callId}`);
                // Not a final event — reply will come later
                return null;
            }
            case 'reply': {
                const data = JSON.parse(event.data);
                // Update channel ID
                if (data.channelId) {
                    this.currentChannelId = data.channelId;
                }
                return {
                    success: true,
                    reply: data.reply,
                    channelId: data.channelId,
                    messageId: data.messageId,
                    suggestions: data.suggestions,
                    usage: data.usage,
                    credits: data.credits,
                };
            }
            case 'error': {
                const data = JSON.parse(event.data);
                return {
                    success: false,
                    error: data.message,
                };
            }
            case 'tool_confirmation_required': {
                // Backend is asking the user to confirm a dangerous tool call
                const request = JSON.parse(event.data);
                console.log(`[SDKAdapter] Confirmation required for "${request.toolName}": ${request.description}`);
                // Show confirmation UI and wait for user decision
                let decision;
                if (this.confirmationHandler) {
                    decision = await this.confirmationHandler(request.toolName, request.description, request.arguments);
                }
                else {
                    // Fallback: auto-reject if no handler registered
                    console.warn('[SDKAdapter] No confirmation handler registered, auto-rejecting');
                    decision = {
                        approved: false,
                        comment: 'No confirmation UI available',
                    };
                }
                console.log(`[SDKAdapter] User ${decision.approved ? 'APPROVED' : 'REJECTED'} "${request.toolName}"${decision.comment ? ` (comment: ${decision.comment})` : ''}`);
                // Send decision back to backend
                await this.sendConfirmation(request.streamId, request.callId, decision);
                // Not a final event — stream continues after confirmation
                return null;
            }
            case 'tool_confirmation_result':
                // Backend acknowledges the confirmation decision — nothing to do on SDK side
                return null;
            case 'execution_plan':
            case 'step_update':
            case 'step_added':
            case 'plan_complete':
            case 'tool_progress':
                this.emitProgress({
                    type: event.event,
                    data: JSON.parse(event.data),
                });
                return null;
            case 'done':
                // Stream complete — result should already be set from 'reply' event
                return null;
            default:
                // Unknown event type, ignore
                return null;
        }
    }
    /**
     * Send a tool execution result back to the backend.
     * The backend is waiting for this to continue the LLM loop.
     * Retries once after a short delay if the first attempt fails (race condition guard).
     */
    async sendToolResult(streamId, callId, result) {
        const url = `${this.apiBaseUrl}/api/chat/tools/${streamId}/${callId}/result`;
        const headers = this.getAuthHeaders();
        const body = JSON.stringify(result);
        for (let attempt = 0; attempt < 2; attempt++) {
            try {
                const response = await fetch(url, { method: 'POST', headers, body });
                if (response.ok)
                    return;
                if (attempt === 0 && response.status === 404) {
                    // Pending entry may not exist yet — retry after a short delay
                    console.warn(`[SDKAdapter] Tool result POST returned 404, retrying in 500ms...`);
                    await new Promise((r) => setTimeout(r, 500));
                    continue;
                }
                console.error('[SDKAdapter] Failed to send tool result:', response.status);
            }
            catch (error) {
                console.error('[SDKAdapter] Error sending tool result:', error);
            }
            break;
        }
    }
    /**
     * Send a confirmation decision back to the backend for a dangerous tool call.
     */
    async sendConfirmation(streamId, callId, decision) {
        try {
            const headers = this.getAuthHeaders();
            const response = await fetch(`${this.apiBaseUrl}/api/chat/tools/${streamId}/${callId}/confirm`, {
                method: 'POST',
                headers,
                body: JSON.stringify(decision),
            });
            if (!response.ok) {
                console.error('[SDKAdapter] Failed to send confirmation:', response.status);
            }
        }
        catch (error) {
            console.error('[SDKAdapter] Error sending confirmation:', error);
        }
    }
    /**
     * Populate the context cache from a tool execution result.
     * Read tools add entries; write tools update existing entries.
     */
    populateCacheFromToolResult(toolName, args, result) {
        if (!this.contextCache)
            return;
        const data = result;
        // Helper: unwrap SDK envelope { data: ..., status: "ok" } for new tools.
        // Old tools (getViewData, getModelByUnnyId) return raw data without envelope.
        const unwrap = (raw) => {
            if (raw && typeof raw === 'object' && 'status' in raw && 'data' in raw) {
                return raw.data;
            }
            return raw;
        };
        if (toolName === 'getViewData' && data) {
            const unnyId = args.unnyId || data.unnyId;
            if (unnyId) {
                this.contextCache.set(`view:${unnyId}`, data, `"${data.name || 'View'}" view`);
            }
        }
        if (toolName === 'getModelByUnnyId' && data) {
            const unnyId = args.unnyId;
            if (unnyId) {
                this.contextCache.set(`entity:${unnyId}`, data, `"${data.Name || ''}" (${unnyId})`);
            }
        }
        if (toolName === 'updateViewData' && data?.success) {
            const unnyId = args.unnyId || data.unnyId;
            if (unnyId) {
                // Cache the data that was just written
                this.contextCache.set(`view:${unnyId}`, args.viewData || data, `"${data.name || 'View'}" view`);
            }
        }
        if (toolName === 'updateModel' && data) {
            const unnyId = args.$unnyId || args.unnyId;
            if (unnyId) {
                this.contextCache.set(`entity:${unnyId}`, data, `"${data.Name || ''}" (${unnyId})`);
            }
        }
        // --- Scripts ---
        if (toolName === 'getScriptCode') {
            const scriptData = unwrap(data);
            if (scriptData) {
                const scriptId = scriptData.id || args.id;
                if (scriptId) {
                    this.contextCache.set(`script:${scriptId}`, scriptData, `"${scriptData.name || 'Script'}" script`);
                }
            }
        }
        // --- Asset metadata ---
        if (toolName === 'getImageAsset') {
            const imageData = unwrap(data);
            if (imageData) {
                const imageId = imageData.id || args.id;
                if (imageId) {
                    this.contextCache.set(`image:${imageId}`, imageData, `"${imageData.name || 'Image'}" image`);
                }
            }
        }
        if (toolName === 'getImages') {
            const imagesData = unwrap(data);
            const images = Array.isArray(imagesData)
                ? imagesData
                : imagesData?.images && Array.isArray(imagesData.images)
                    ? imagesData.images
                    : null;
            if (images) {
                for (const image of images) {
                    if (image?.id) {
                        this.contextCache.set(`image:${image.id}`, image, `"${image.name || 'Image'}" image`);
                    }
                }
            }
        }
        if (toolName === 'getViewMetadata') {
            const metadata = unwrap(data);
            if (metadata) {
                const viewId = metadata.unnyId || args.unnyId;
                if (viewId) {
                    this.contextCache.set(`view-metadata:${viewId}`, metadata, `"${metadata.name || 'View'}" view metadata`);
                }
            }
        }
        // Write tools for scripts: backend handles cache update, SDK just evicts stale
        if (toolName === 'updateScriptCode') {
            // Backend updates the cache entry; SDK evicts so next refresh re-fetches
            const scriptId = args.id;
            if (scriptId) {
                this.contextCache.delete(`script:${scriptId}`);
            }
        }
        if (toolName === 'editScriptCode') {
            const scriptId = args.id;
            if (scriptId) {
                // Edits applied but we don't have the full updated code — evict
                this.contextCache.delete(`script:${scriptId}`);
            }
        }
        // --- Localizations ---
        if (toolName === 'getLocalizationStringByKey') {
            const locData = unwrap(data);
            if (locData) {
                const locKey = args.key;
                if (locKey) {
                    this.contextCache.set(`localization:${locKey}`, locData, `localization "${locKey}"`);
                }
            }
        }
        if (toolName === 'getLocalizationStrings') {
            const locUnwrapped = unwrap(data);
            // SDK may return { strings: [...] } or a raw array
            const locArray = Array.isArray(locUnwrapped)
                ? locUnwrapped
                : locUnwrapped?.strings && Array.isArray(locUnwrapped.strings)
                    ? locUnwrapped.strings
                    : null;
            if (locArray) {
                for (const entry of locArray) {
                    const entryKey = `${entry.group}/${entry.key}`;
                    this.contextCache.set(`localization:${entryKey}`, entry, `localization "${entryKey}"`);
                }
            }
        }
        // Write tools for localizations: backend handles cache merge, SDK just sets result
        if (toolName === 'updateLocalizationString') {
            const updatedLoc = unwrap(data);
            if (updatedLoc) {
                const locKey = args.key;
                if (locKey) {
                    this.contextCache.set(`localization:${locKey}`, updatedLoc, `localization "${locKey}"`);
                }
            }
        }
        if (toolName === 'createLocalizationString') {
            const createdLoc = unwrap(data);
            if (createdLoc) {
                const group = args.group;
                const key = args.key;
                if (group && key) {
                    const locKey = `${group}/${key}`;
                    this.contextCache.set(`localization:${locKey}`, createdLoc, `localization "${locKey}"`);
                }
            }
        }
        // --- Templates ---
        if (toolName === 'getTemplateList') {
            const templateArray = unwrap(data);
            if (Array.isArray(templateArray)) {
                for (const template of templateArray) {
                    if (template.name) {
                        this.contextCache.set(`template:${template.name}`, template, `template "${template.displayName || template.name}"`);
                    }
                }
            }
        }
        if (toolName === 'createTemplate') {
            const templateData = unwrap(data);
            if (templateData?.name) {
                this.contextCache.set(`template:${templateData.name}`, templateData, `template "${templateData.displayName || templateData.name}"`);
            }
        }
        // Write tools for templates: backend handles cache update
        // addTemplateParam/removeTemplateParam — evict so next refresh re-fetches
        if (toolName === 'addTemplateParam' || toolName === 'removeTemplateParam') {
            const templateName = args.templateName;
            if (templateName) {
                this.contextCache.delete(`template:${templateName}`);
            }
        }
        // --- Products ---
        if (toolName === 'getProducts') {
            const productsData = unwrap(data);
            if (productsData) {
                const count = Array.isArray(productsData) ? productsData.length : 0;
                this.contextCache.set('products:all', productsData, `all products (${count})`);
            }
        }
        // Write tools for products: backend handles cache update
        // createProduct/updateProduct/deleteProduct — evict so next refresh re-fetches
        if (toolName === 'createProduct' ||
            toolName === 'updateProduct' ||
            toolName === 'deleteProduct') {
            this.contextCache.delete('products:all');
        }
    }
    /**
     * Sync the client-side tool catalog to the backend.
     * Called by BalancyAISDK.registerTools() whenever tools are registered.
     *
     * Only sends names, descriptions, and parameter schemas — never handler functions.
     */
    async syncToolCatalog(catalog, contractVersion) {
        // Store catalog so we can re-sync before each SSE request
        this.lastToolCatalog = catalog;
        this.lastToolContractVersion = contractVersion;
        try {
            const headers = this.getAuthHeaders();
            const response = await fetch(`${this.apiBaseUrl}/api/chat/tools/register`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ tools: catalog, contractVersion }),
            });
            if (response.ok) {
                this.toolCatalogSynced = catalog.length > 0;
                console.log(`[SDKAdapter] Tool catalog synced: ${catalog.length} tools`);
            }
            else {
                console.error('[SDKAdapter] Failed to sync tool catalog:', response.status);
            }
        }
        catch (error) {
            console.error('[SDKAdapter] Error syncing tool catalog:', error);
        }
    }
    /**
     * Set current channel ID (for channel switching).
     * Clears the cache when switching channels or starting a new one —
     * stale data from a previous channel must not leak into a new context.
     */
    setCurrentChannel(channelId) {
        const previousChannelId = this.currentChannelId;
        this.currentChannelId = channelId;
        if (this.contextCache) {
            // Clear cache when switching to a different channel or starting fresh
            if (channelId !== previousChannelId) {
                this.contextCache.clear();
                console.log(`[SDKAdapter] Cache cleared (channel switch: ${previousChannelId} → ${channelId})`);
            }
            if (channelId) {
                this.contextCache.setChannel(channelId);
            }
        }
    }
    /**
     * Get current channel ID
     */
    getCurrentChannel() {
        return this.currentChannelId;
    }
    /**
     * Get the context optimization flag
     */
    getContextOptimized() {
        return this.contextOptimized;
    }
    /**
     * Set the context optimization flag.
     * When off, cache is not sent with messages and not refreshed.
     */
    setContextOptimized(value) {
        this.contextOptimized = value;
    }
    /**
     * Get the context cache instance (for external cache population)
     */
    getContextCache() {
        return this.contextCache;
    }
    /**
     * Restore cache from persisted keys (called after loading a channel)
     */
    async restoreCacheFromKeys(keys) {
        if (!this.contextCache || !this.contextOptimized)
            return;
        this.contextCache.clear();
        if (keys.length > 0) {
            await this.contextCache.restoreFromKeys(keys);
        }
    }
    /**
     * Notify the backend that entities were saved and IDs changed.
     * Remaps cache keys locally and sends the mapping to the server
     * so the LLM is informed of the new IDs.
     */
    async notifyEntitiesSaved(idMapping) {
        if (this.contextCache) {
            const remapped = this.contextCache.remapEntityIds(idMapping);
            if (remapped.length > 0) {
                console.log(`[SDKAdapter] Remapped ${remapped.length} entity IDs:`, remapped);
            }
        }
        if (!this.currentChannelId)
            return;
        try {
            const headers = this.getAuthHeaders();
            await fetch(`${this.apiBaseUrl}/api/chat/entities-saved`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    channelId: this.currentChannelId,
                    idMapping,
                }),
            });
        }
        catch (err) {
            console.warn('[SDKAdapter] Failed to notify backend of entity save:', err);
        }
    }
    /**
     * Health check - ping the API server
     * Returns true if server is reachable, false otherwise
     */
    async healthCheck() {
        try {
            const response = await fetch(`${this.apiBaseUrl}/api/health`, {
                method: 'GET',
                signal: AbortSignal.timeout(5000),
            });
            return response.ok;
        }
        catch {
            return false;
        }
    }
    getAssetURL(path) {
        // Return CDN URL or fall back to relative path
        return `${this.assetsBaseUrl}/${path}`;
    }
    getUser() {
        return this.user;
    }
    async getAccessToken() {
        return this.jwtToken || this.accessToken;
    }
    /**
     * Set user and access token (called by Balancy after auth)
     */
    setAuth(user, accessToken) {
        this.user = user;
        this.accessToken = accessToken;
    }
    /**
     * Exchange a Balancy S2S token for an internal JWT.
     * Called once during authenticate() so subsequent requests use
     * Authorization: Bearer <jwt> instead of per-request S2S validation.
     *
     * Returns `{ success: true }` on success, or `{ success: false, error, reason }` on failure.
     */
    async exchangeBalancyToken(email, balancyToken) {
        try {
            const response = await fetch(`${this.apiBaseUrl}/api/auth/balancy`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, token: balancyToken }),
            });
            if (!response.ok) {
                console.warn('[SDKAdapter] Balancy token exchange failed:', response.status);
                const reason = response.status === 401
                    ? 'token_invalid'
                    : 'server_unavailable';
                const errorData = await response.json().catch(() => ({}));
                const message = errorData?.error?.message || `Server returned ${response.status}`;
                return { success: false, error: message, reason };
            }
            const data = await response.json();
            if (data.data?.tokens?.accessToken) {
                this.jwtToken = data.data.tokens.accessToken;
                const needsTermsAcceptance = !!data.data.needsTermsAcceptance;
                console.log('[SDKAdapter] Balancy token exchanged for JWT successfully', { needsTermsAcceptance });
                return { success: true, needsTermsAcceptance };
            }
            console.warn('[SDKAdapter] Balancy token exchange: no accessToken in response');
            return {
                success: false,
                error: 'No access token in response',
                reason: 'unknown',
            };
        }
        catch (error) {
            console.warn('[SDKAdapter] Balancy token exchange error:', error);
            return {
                success: false,
                error: 'Could not connect to authentication server',
                reason: 'server_unavailable',
            };
        }
    }
    /**
     * Accept the current terms version on the server.
     * Called after the user clicks "Get Started" on the terms/welcome screen.
     */
    async acceptTerms() {
        try {
            const headers = this.getAuthHeaders();
            const response = await fetch(`${this.apiBaseUrl}/api/auth/accept-terms`, {
                method: 'POST',
                headers,
            });
            if (response.ok) {
                console.log('[SDKAdapter] Terms accepted successfully');
                return true;
            }
            console.warn('[SDKAdapter] Accept terms failed:', response.status);
            return false;
        }
        catch (error) {
            console.warn('[SDKAdapter] Accept terms error:', error);
            return false;
        }
    }
    /**
     * Set additional user context data (name, apiGameId, publicKey, url, simplifiedUrl, platform)
     * Merges with existing data so UI-set fields (like platform) are preserved
     * when SDK updates other fields (like apiGameId, publicKey, etc.)
     */
    setUserContextData(contextData) {
        this.userContextData = { ...this.userContextData, ...contextData };
        console.log('[SDKAdapter] User context data updated:', this.userContextData);
    }
    async requestAuth() {
        // In SDK mode, auth is handled by Balancy.dev
        // We just return a message telling them to authenticate through Balancy
        return {
            success: false,
            error: 'Please sign in through Balancy.dev',
        };
    }
    async logout() {
        this.user = null;
        this.accessToken = null;
        this.jwtToken = null;
        this.toolCatalogSynced = false;
        this.lastToolCatalog = [];
        this.lastToolContractVersion = undefined;
        console.log('[SDKAdapter] Logged out');
    }
    async checkGameSyncStatus(apiGameId, branchName) {
        try {
            const response = await fetch(`${this.apiBaseUrl}/api/games/${apiGameId}/branches/${branchName}/status`, {
                headers: {
                    'Content-Type': 'application/json',
                    ...(this.accessToken
                        ? { Authorization: `Bearer ${this.accessToken}` }
                        : {}),
                },
            });
            if (response.ok) {
                const data = await response.json();
                return data.data;
            }
            else if (response.status === 404) {
                return {
                    gameExists: false,
                    branchExists: false,
                    gameInfo: null,
                    syncRequired: true,
                };
            }
            else {
                console.error('[SDKAdapter] Failed to check sync status:', response.status);
                return {
                    gameExists: false,
                    branchExists: false,
                    gameInfo: null,
                    syncRequired: true,
                };
            }
        }
        catch (error) {
            console.error('[SDKAdapter] Error checking sync status:', error);
            return {
                gameExists: false,
                branchExists: false,
                gameInfo: null,
                syncRequired: true,
            };
        }
    }
    async startGameSync() {
        // In SDK mode, game sync is handled differently
        // Balancy.dev would need to provide credentials directly
        console.log('[SDKAdapter] Game sync requested - please configure through Balancy.dev settings');
        this.addBotMessage('Game sync is managed through Balancy.dev settings. Please contact support.');
    }
    async startBranchSync() {
        const context = this.getContext();
        if (!context || !context.apiGameId || !context.branchName) {
            throw new Error('No game/branch context available for sync');
        }
        console.log('[SDKAdapter] Starting branch sync');
        this.addBotMessage('Starting branch sync...');
        try {
            const response = await fetch(`${this.apiBaseUrl}/api/games/${context.apiGameId}/branches/${context.branchName}/sync`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(this.accessToken
                        ? { Authorization: `Bearer ${this.accessToken}` }
                        : {}),
                },
                body: JSON.stringify({
                    apiGameId: context.apiGameId,
                    branchName: context.branchName,
                    url: context.url,
                }),
            });
            if (response.ok) {
                const result = await response.json();
                console.log('[SDKAdapter] Branch sync started:', result);
                this.addBotMessage('Branch sync completed successfully!');
            }
            else {
                console.error('[SDKAdapter] Branch sync failed:', response.status);
                this.addBotMessage('Branch sync failed. Please try again.');
            }
        }
        catch (error) {
            console.error('[SDKAdapter] Error starting branch sync:', error);
            this.addBotMessage('Connection error. Please check your network and try again.');
        }
    }
    addBotMessage(message) {
        // Dispatch custom event that the UIManager can listen to
        window.dispatchEvent(new CustomEvent('balancy-add-bot-message', {
            detail: { message },
        }));
    }
    getApiBaseUrl() {
        return this.apiBaseUrl;
    }
}
